import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, IconButton, Tooltip
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

const STATUS_LABEL = {
  open: { label: 'Open', color: 'success' },
  soft_closed: { label: 'Soft-closed', color: 'warning' },
  hard_closed: { label: 'Hard-closed', color: 'error' }
};

function formatMoney(n) {
  if (!n) return '-';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(Number(n));
}

export default function PeriodsManager() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);  // { period, action }
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem('token');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accounting/periods`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (res.data.success) setPeriods(res.data.data);
      else setError(res.data.error || 'Failed to load periods');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doAction = async () => {
    if (!confirmDialog) return;
    const { period, action } = confirmDialog;
    setBusy(true);
    try {
      const url = `${API_URL}/api/accounting/periods/${period.period_name}/${action === 'reopen' ? 'reopen' : 'close'}`;
      const body = action === 'reopen' ? {} : { level: action };  // 'soft' or 'hard'
      const res = await axios.post(url, body, { headers: { Authorization: `Bearer ${token()}` } });
      if (res.data.success) {
        setToast({ msg: res.data.message, severity: 'success' });
        await load();
      } else {
        setToast({ msg: res.data.error || 'Action failed', severity: 'error' });
      }
    } catch (e) {
      setToast({ msg: e.response?.data?.error || e.message, severity: 'error' });
    } finally {
      setBusy(false);
      setConfirmDialog(null);
    }
  };

  if (loading) return (<Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>);
  if (error) return (<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>);

  // Only show periods that have JEs, plus the next 3 future periods for planning.
  const withJEs = periods.filter(p => p.je_count > 0);
  const futureOpen = periods
    .filter(p => p.je_count === 0 && p.status === 'open')
    .slice(-3)
    .reverse();
  const shown = [...withJEs, ...futureOpen];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Accounting Periods</Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={load}><RefreshIcon /></IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>Open</strong> periods accept new JEs.
        <strong> Soft-closed</strong> periods accept writes but log a warning (month-end adjustments).
        <strong> Hard-closed</strong> periods reject all new JEs — use only after the period is fully reviewed.
      </Alert>

      <Card>
        <CardContent>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell align="right">JE Count</TableCell>
                  <TableCell align="right">Total Debits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Closed By / At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map(p => {
                  const s = STATUS_LABEL[p.status] || { label: p.status, color: 'default' };
                  return (
                    <TableRow key={p.period_name} hover>
                      <TableCell><strong>{p.period_name}</strong></TableCell>
                      <TableCell align="right">{p.je_count.toLocaleString()}</TableCell>
                      <TableCell align="right">{formatMoney(p.total_debits)}</TableCell>
                      <TableCell><Chip size="small" label={s.label} color={s.color} /></TableCell>
                      <TableCell>
                        {p.status === 'hard_closed' && p.hard_closed_by
                          ? `${p.hard_closed_by} at ${p.hard_closed_at}`
                          : p.status === 'soft_closed' && p.soft_closed_by
                          ? `${p.soft_closed_by} at ${p.soft_closed_at}`
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {p.status === 'open' && (
                          <Button size="small" startIcon={<WarningAmberIcon />} color="warning"
                                  onClick={() => setConfirmDialog({ period: p, action: 'soft' })}>
                            Soft close
                          </Button>
                        )}
                        {p.status === 'soft_closed' && (
                          <>
                            <Button size="small" startIcon={<LockIcon />} color="error"
                                    onClick={() => setConfirmDialog({ period: p, action: 'hard' })}>
                              Hard close
                            </Button>
                            <Button size="small" startIcon={<LockOpenIcon />}
                                    onClick={() => setConfirmDialog({ period: p, action: 'reopen' })} sx={{ ml: 1 }}>
                              Reopen
                            </Button>
                          </>
                        )}
                        {p.status === 'hard_closed' && (
                          <Button size="small" startIcon={<LockOpenIcon />}
                                  onClick={() => setConfirmDialog({ period: p, action: 'reopen' })}>
                            Reopen
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle>
          {confirmDialog?.action === 'reopen' ? 'Reopen' : confirmDialog?.action === 'hard' ? 'Hard-close' : 'Soft-close'}
          {' '}period {confirmDialog?.period?.period_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDialog?.action === 'hard'
              ? 'This will REJECT all future JE writes to this period. Use only after the period is fully reviewed.'
              : confirmDialog?.action === 'soft'
              ? 'Soft-close allows writes but logs a warning. Useful during month-end adjustments.'
              : 'Reopen will set the period back to open, allowing writes without warnings.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} disabled={busy}>Cancel</Button>
          <Button onClick={doAction} variant="contained" disabled={busy}
                  color={confirmDialog?.action === 'hard' ? 'error' : confirmDialog?.action === 'soft' ? 'warning' : 'primary'}>
            {busy ? 'Working…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        {toast && <Alert severity={toast.severity}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
